<script>
import { Line } from 'vue-chartjs';
import zoom from 'chartjs-plugin-zoom';

export default {
    name: 'ReportChartComponent',
    extends: Line,
    props: {
        data: {
            type: [Array, Object],
            required: true,
            default: null,
        },
        meta: {
            type: Object,
            required: false,
            default: null,
        },
        combo: {
            type: Boolean,
            required: false,
            default: false,
        },
        color: {
            type: String,
            required: false,
            default: null,
        },
        showGrid: {
            type: Boolean,
            required: false,
            default: false,
        },
        height: {
            type: Number,
            require: false,
            default: 400,
        },
    },
    data() {
        return {
            chart: null,
            initialTicksOptions: {},
            datasets: [],
            timeFormat: {},
            colors: {
                red: '232,83,95',
                green: '17,179,114',
                amber: '192,132,42',
                blue: '30,121,173',
            },
            gradients: {
                red: null,
                green: null,
                amber: null,
                blue: null,
            },
        };
    },
    watch: {
        data: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.prepareData();
                this.render();
            }
        },
    },
    created() {
        window.addEventListener('resize', this.onResize);
    },
    destroyed() {
        window.removeEventListener('resize', this.onResize);
    },
    mounted() {
        this.prepareGradient();
        this.prepareData();
        this.addPlugin(zoom);
        this.render();
    },
    methods: {
        prepareGradient() {
            Object.keys(this.gradients).forEach((gradient) => {
                this.gradients[gradient] = this.$refs.canvas.getContext('2d').createLinearGradient(0, 0, 0, 150);
                this.gradients[gradient].addColorStop(0, `rgba(${this.colors[gradient]}, 0.5)`);
                this.gradients[gradient].addColorStop(0.5, `rgba(${this.colors[gradient]}, 0.25)`);
                this.gradients[gradient].addColorStop(1, `rgba(${this.colors[gradient]}, 0)`);
            });
        },
        modifyData(data) {
            const newData = data.map((item) => {
                return { x: new Date(item.start_date), y: item.total };
            });
            return newData;
        },
        prepareData() {
            this.datasets = [];
            if (!this.combo) {
                const currentChartData = {
                    borderColor: `rgba(${this.colors[this.color]}, 0.75)`,
                    pointBackgroundColor: `rgba(${this.colors[this.color]}, 0.75)`,
                    borderWidth: 2,
                    pointBorderColor: `rgba(${this.colors[this.color]}, 0.75)`,
                    radius: 1,
                    // lineTension: 0.6,
                    // cubicInterpolationMode: 'monotone',
                    // backgroundColor: this.gradients[this.color],
                    backgroundColor: 'transparent',
                    data: this.modifyData(this.data),
                    pointRadius: 1,
                    pointHoverRadius: 3,
                    pointHitRadius: 5,
                    label: t('reports::grid.title.blood_results'),
                };
                this.datasets.push(currentChartData);
            }
            if (this.combo) {
                Object.keys(this.data).forEach((key) => {
                    let currentChartColor;
                    switch (key) {
                        case 'red_patients':
                            currentChartColor = 'red';
                            break;
                        case 'amber_patients':
                            currentChartColor = 'amber';
                            break;
                        case 'astm_devices':
                            currentChartColor = 'green';
                            break;
                        case 'overdue_patients':
                            currentChartColor = 'blue';
                            break;
                        default:
                            currentChartColor = 'blue';
                            break;
                    }
                    const currentChartData = {
                        borderColor: `rgba(${this.colors[currentChartColor]}, 0.75)`,
                        pointBackgroundColor: `rgba(${this.colors[currentChartColor]}, 0.75)`,
                        borderWidth: 2,
                        pointBorderColor: `rgba(${this.colors[currentChartColor]}, 0.75)`,
                        radius: 1,
                        fill: true,
                        // lineTension: 0.6,
                        cubicInterpolationMode: 'monotone',
                        // backgroundColor: this.gradients[currentChartColor],
                        backgroundColor: 'transparent',
                        data: this.modifyData(this.data[key]),
                        pointRadius: 1,
                        pointHoverRadius: 3,
                        pointHitRadius: 5,
                        label: t('reports::grid.title.blood_results'),
                    };
                    if (currentChartData.data && currentChartData.data.length && key !== 'all_patients') {
                        this.datasets.push(currentChartData);
                    }
                });
            }
            this.meta ? this.calcXFormats({ ...this.meta }) : false;
        },
        calcXFormats(meta) {
            const grouping = meta.grouping;
            let unit;
            // let unitStepSize;
            // let round;
            switch (grouping) {
                case 'hour':
                    unit = 'day';
                    break;
                case 'day':
                    unit = 'day';
                    break;
                case 'week':
                    unit = 'day';
                    break;
                case 'month':
                    unit = 'month';

                    break;
            }
            this.timeFormat = {
                unit,
                // unitStepSize: 2,
                tooltipFormat: 'LL',
                displayFormats: {
                    hour: 'DD.MM.YY',
                    day: 'DD.MM.YY',
                    week: 'DD.MM.YY',
                    month: 'DD.MM.YY',
                },
            };
        },
        render() {
            this.renderChart(
                {
                    datasets: this.datasets,
                },
                {
                    responsive: true,
                    responsiveAnimationDuration: 100,
                    animationDuration: 400,
                    maintainAspectRatio: false,
                    showScale: false,
                    legend: false,
                    position: 'relative',
                    onResize: this.onResize(),
                    scaleBeginAtZero: false,
                    barBeginAtOrigin: true,
                    layout: {
                        padding: {
                            top: 10,
                            right: 0,
                        },
                    },
                    scales: {
                        xAxes: [
                            {
                                offset: true,
                                type: 'time',
                                stacked: false,
                                time: this.timeFormat,
                                display: this.showGrid,
                                beginAtZero: true,
                                scaleLabel: {
                                    display: false,
                                    labelString: t('reports::graph.label.date'),
                                },
                                gridLines: {
                                    offsetGridLines: false,
                                },
                                ticks: {
                                    align: 'start',
                                    autoSkip: false,
                                    fontColor: '#a5a5a5',
                                    fontSize: 12,
                                    fontStyle: 'normal',
                                    fontFamily: 'Roboto',
                                    // maxRotation: 0
                                },
                            },
                        ],
                        yAxes: [
                            {
                                display: this.showGrid,
                                ticks: {
                                    autoSkip: false,
                                    maxRotation: 0,
                                    precision: 0,
                                    beginAtZero: true,
                                    fontColor: '#a5a5a5',
                                    fontSize: 12,
                                    fontStyle: 'normal',
                                    fontFamily: 'Roboto',
                                    display: this.showGrid,
                                    // stepSize: 2,
                                    // callback: function (value) {
                                    //     if (value % 1 === 0) {
                                    //         return value;
                                    //     }
                                    // },
                                },
                            },
                        ],
                    },
                }
            );
        },
        onResize() {
            _.delay(() => {
                this.initialTicksOptions = this.$data._chart.options.scales.xAxes[0].ticks;
                this.$refs.canvas.parentNode.style.position = 'relative';
                this.$refs.canvas.parentNode.style.width = '100%';
                // this.$refs.canvas.style.height = this.combo ? '300px' : '200px';
                this.$refs.canvas.style.height = `${this.height}px`;
                this.$refs.canvas.style.width = '100%';
                this.$data._chart.options.scales.xAxes[0].ticks = this.initialTicksOptions;
                this.$data._chart.update();
            }, 100);
        },
    },
};
</script>
